import { Component, EventEmitter, input, Input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Utils } from 'app/utils.class';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProject } from '../customer-project.interface';
import { Filter } from '../filter';
import { LocalStorage } from '../local-storage.class';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { Features } from '../services/auth/features';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { Status } from '../status.interface';
import { FieldOptionsService } from '../workarea/shared/field-options.service';

@Component({
    selector: 'app-workareas-filter',
    templateUrl: './workareas-filter.component.html',
    standalone: false,
})
export class WorkareasFilterComponent extends CodaltComponent implements OnInit {
    @Input() customer: Customer;
    workareas = input([]);
    readonly workareas$ = toObservable(this.workareas);

    fieldOptions = {};
    filter = new Filter();
    filterChange = new EventEmitter();
    closeFilter = new EventEmitter();
    projects: CustomerProject[];
    statuses: Status[];

    Utils = Utils;

    constructor(
        private fieldOptionService: FieldOptionsService,
        private customerProjectService: CustomerProjectService,
        private workareaService: WorkareaService,
        private router: Router,
        private activatedRoute: ActivatedRoute
    ) {
        super();
    }

    ngOnInit() {
        this.workareaService
            .getAvailableStatuses(this.customer.customer_type)
            .then(statuses => (this.statuses = statuses));
        this.filter = new Filter();
        this.subscriptions.add(
            this.activatedRoute.queryParams
                .pipe(startWith(this.activatedRoute.snapshot.queryParams))
                .subscribe(params => {
                    if (Object.keys(params).length > 0) {
                        if (!!params['resetfilter']) {
                            this.filter = new Filter();
                            this.inputChanged();
                        } else if (JSON.stringify(params) !== JSON.stringify(this.filter.forParams())) {
                            const newParams = structuredClone(params);
                            newParams.startDate = newParams.startDate ? new Date(+newParams.startDate) : null;
                            newParams.endDate = newParams.endDate ? new Date(+newParams.endDate) : null;
                            delete newParams['movingWorkarea'];
                            delete newParams['newLat'];
                            delete newParams['newLng'];
                            newParams.statuses =
                                newParams.statuses
                                    ?.split(',')
                                    .filter(s => !!s)
                                    .map(s => +s) ?? [];
                            Object.assign(this.filter, newParams);
                            const rawWorkareaIds =
                                newParams['workarea_ids'] ?? newParams['workareaIds'] ?? newParams['workareasIds'];
                            this.filter.workarea_ids =
                                rawWorkareaIds && !['undefined', 'null'].includes(String(rawWorkareaIds).toLowerCase())
                                    ? String(rawWorkareaIds)
                                          .split(',')
                                          .map(n => +n)
                                          .filter(n => n > 0)
                                    : [];
                            this.filter.project = this.filter.project ? +this.filter.project : null;
                            if (this.filter.klinkerfloor !== null) {
                                this.filter.klinkerfloor =
                                    this.filter.klinkerfloor === 'true' || this.filter.klinkerfloor === true;
                            }
                            this.filter.min = this.filter.min ? +this.filter.min : null;
                            this.filter.max = this.filter.max ? +this.filter.max : null;
                            LocalStorage.setFilter(this.filter);
                        }
                    } else {
                        const storageFilter = LocalStorage.getFilter();

                        if (storageFilter) {
                            delete storageFilter['movingWorkarea'];
                            delete storageFilter['newLat'];
                            delete storageFilter['newLng'];

                            Object.assign(this.filter, storageFilter);
                            this.inputChanged();
                        }
                    }
                })
        );

        this.filterChange.emit(this.filter);

        this.subscriptions.add(
            combineLatest([this.fieldOptionService.getOptions(), this.workareas$])
                .pipe(
                    map(([options, workareas]) => {
                        const transformedOptions = { ...options };

                        Object.keys(transformedOptions).forEach(key => {
                            if (Array.isArray(transformedOptions[key])) {
                                transformedOptions[key] = [...transformedOptions[key]];
                            }
                        });

                        if (!workareas?.length) return transformedOptions;

                        const waymarksFromOptions = (transformedOptions['waymark'] ?? []).filter(o =>
                            workareas.some(w => w.extra?.waymark === o)
                        );

                        const waymarksFromWorkareas = workareas.map(w => w.extra?.waymark).filter(Boolean);

                        transformedOptions['waymark'] = [
                            ...new Set([...waymarksFromOptions, ...waymarksFromWorkareas]),
                        ];

                        return transformedOptions;
                    })
                )
                .subscribe(options => (this.fieldOptions = options))
        );

        if (AuthorisationService.hasFeature(Features.CustomerProjects)) {
            this.customerProjectService
                .getProjects(false, this.customer.id)
                .subscribe(projects => (this.projects = projects));
        } else {
            this.projects = [];
        }
    }

    inputChanged() {
        LocalStorage.setFilter(this.filter);
        this.router.navigate([], {
            queryParams: this.filter.forParams(),
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
        this.filterChange.emit(this.filter);
    }

    applyFilterButton() {
        this.inputChanged();
        this.closeFilter.emit();
    }

    resetFilter() {
        this.filter = new Filter();
        this.inputChanged();
    }

    get filteredProjects(): CustomerProject[] {
        if (!this.projects) {
            return [];
        }
        if (!this.filter.workareaType) {
            return this.projects;
        }
        if (this.filter.workareaType === 'Werkvakken') {
            return this.projects.filter(project => project.type === 'maintenance' || project.type === 'regular');
        }
        if (this.filter.workareaType === 'Asfaltboringen') {
            return this.projects.filter(project => project.type === 'drillings');
        }
        return this.projects;
    }
}
